import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import StudentDeletionModal from '../components/StudentDeletionModal';
import ChangeBatchModal from '../components/ChangeBatchModal';
import AddEnrollmentModal from '../components/AddEnrollmentModal';
import SearchableSelect from '../components/Form/SearchableSelect';
import { useLoadBatches } from '../hooks/useLoadBatches';
import { useTheme } from '../context/ThemeContext';
import StaffPageShell from '../components/StaffPageShell';
import {
  buildAllStudents,
  filterAndSortStudents,
  getStudentEmail,
  getStudentFullName
} from './studentManagementUtils';
import StudentEnrollmentsSections from '../components/StudentList/StudentEnrollmentsSections';
import StudentDetailExpandedView from '../components/StudentList/StudentDetailExpandedView';
import EnrollmentStatusControl from '../components/StudentList/EnrollmentStatusControl';
import { hasLeftEnrollment } from '../utils/studentEnrollmentStatus';
import ModalFrame from '../components/modals/ModalFrame';
import { useBulkStudentSelection } from '../hooks/useBulkStudentSelection';
import { useEnrollmentStatusUpdates } from '../hooks/useEnrollmentStatusUpdates';
import usePermissions from '../hooks/usePermissions';
import { enrollmentsAPI } from '../services/api';
import { getApiErrorMessage } from '../utils/apiErrorMessage';
import { BULK_DELETE_LIMIT, isAllSelectableSelected } from '../utils/bulkStudentSelection';

const StudentManagementPage = () => {
  const navigate = useNavigate();
  const { currentTheme } = useTheme();
  const { hasPermission } = usePermissions();
  const canDeleteStudents = hasPermission('students.delete');
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const {
    selectedStudents,
    selectionWarning,
    clearSelection,
    toggleStudentSelection,
    toggleAllSelection,
    selectionAtLimit,
    selectionLimit
  } = useBulkStudentSelection();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteStudent, setDeleteStudent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('student');
  const [sortBy, setSortBy] = useState('firstName');
  const [sortOrder, setSortOrder] = useState('asc');
  const [retryCount, setRetryCount] = useState(0);
  const [showSoftDeleted, setShowSoftDeleted] = useState(false);
  const [softDeletedStudents, setSoftDeletedStudents] = useState([]);
  const [changeBatchStudent, setChangeBatchStudent] = useState(null);
  const [addEnrollmentStudent, setAddEnrollmentStudent] = useState(null);
  const [openEnrollmentDetailStudentId, setOpenEnrollmentDetailStudentId] = useState(null);
  const [removingEnrollmentId, setRemovingEnrollmentId] = useState(null);
  const [viewDetailStudent, setViewDetailStudent] = useState(null);
  const { batches: modalBatches, loadBatches } = useLoadBatches();

  const {
    statusUpdateError,
    statusUpdatingIds,
    bulkStatusUpdating,
    handleEnrollmentStatusChange,
    handleBulkMarkAsLeft,
  } = useEnrollmentStatusUpdates((updater) => setStudents(updater), {
    patchSecondaryList: (updater) => setSoftDeletedStudents(updater),
  });

  const TABLE_COL_SPAN = 17;

  // Fetch students
  const fetchStudents = async () => {
    setLoading(true);
    setError('');
    try {
      // Get token from user context (stored in localStorage as 'user')
      const userData = JSON.parse(localStorage.getItem('user'));
      const token = userData?.token;
      
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }
      
      // Use API service layer
      const { studentsAPI } = await import('../services/api');
      const response = await studentsAPI.getAll();
      
      setStudents(response.data || []);
    } catch (err) {
      console.error('❌ Error fetching students:', err);
      console.error('❌ Error response:', err.response?.data);
      console.error('❌ Error status:', err.response?.status);
      console.error('❌ Error headers:', err.response?.headers);
      
      let errorMessage = 'Failed to fetch students';
      if (err.response?.status === 401 || err.message?.includes('authentication token')) {
        errorMessage = 'Authentication failed. Redirecting to login...';
        // Clear invalid token and redirect to login
        localStorage.removeItem('user');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else if (err.response?.status === 403) {
        errorMessage = 'Access denied. Admin role required.';
      } else if (err.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (err.code === 'NETWORK_ERROR') {
        errorMessage = 'Network error. Check your connection.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Fetch soft-deleted students
  const fetchSoftDeletedStudents = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const token = userData?.token;
      
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }

      const { studentsAPI } = await import('../services/api');
      // Use dedicated soft-deleted endpoint
      const response = await studentsAPI.getSoftDeleted();
      
      setSoftDeletedStudents(response.data || []);
    } catch (err) {
      console.error('❌ Error fetching soft-deleted students:', err);
      setSoftDeletedStudents([]);
    }
  };

  // Retry function
  const retryFetch = () => {
    setRetryCount(prev => prev + 1);
    fetchStudents();
  };

  // Toggle soft-deleted students visibility
  const toggleSoftDeleted = () => {
    setShowSoftDeleted(!showSoftDeleted);
    if (!showSoftDeleted) {
      fetchSoftDeletedStudents();
    }
  };

  useEffect(() => {
    fetchStudents();
  }, []);

  useEffect(() => {
    if (showSoftDeleted) {
      fetchSoftDeletedStudents();
    }
  }, [showSoftDeleted]);

  const openChangeBatchModal = async (student) => {
    if (!student?.enrollments?.length) return;
    setChangeBatchStudent(student);
    await loadBatches();
  };

  const removeEnrollment = async (enrollment) => {
    const batchName = enrollment?.batch?.batchName || 'this batch';
    const confirmed = window.confirm(
      `Remove the enrollment in ${batchName}? Fees, sessions, and placement stored on this enrollment are removed. The student keeps the other enrollment.`
    );
    if (!confirmed) return;
    setRemovingEnrollmentId(enrollment.id);
    setError('');
    try {
      await enrollmentsAPI.delete(enrollment.id);
      await fetchStudents();
    } catch (err) {
      setError(getApiErrorMessage(err) || 'Could not remove the enrollment');
    } finally {
      setRemovingEnrollmentId(null);
    }
  };

  const openAddEnrollmentModal = async (student) => {
    if (!student?.id) return;
    setAddEnrollmentStudent(student);
    await loadBatches();
  };

  // Handle single student delete
  const handleDeleteStudent = (student) => {
    setDeleteStudent(student);
    setShowDeleteModal(true);
  };

  // Handle bulk delete
  const handleBulkDelete = () => {
    if (selectedStudents.length === 0) return;
    if (selectedStudents.length > BULK_DELETE_LIMIT) return;
    setDeleteStudent(null);
    setShowDeleteModal(true);
  };

  // Handle delete success
  const handleDeleteSuccess = () => {
    fetchStudents();
    if (showSoftDeleted) {
      fetchSoftDeletedStudents();
    }
    clearSelection();
    setShowDeleteModal(false);
    setDeleteStudent(null);
  };

  // Handle delete error
  const handleDeleteError = (err) => {
    console.error('Delete failed:', err);
  };

  const allStudents = buildAllStudents(students, softDeletedStudents, showSoftDeleted);
  const filteredStudents = filterAndSortStudents(allStudents, { searchTerm, sortBy, sortOrder });
  const allSelectableSelected = isAllSelectableSelected(
    selectedStudents,
    filteredStudents,
    selectionLimit
  );

  if (loading) {
    return (
      <StaffPageShell title="Manage Students" wide>
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">Loading students...</div>
        </div>
      </StaffPageShell>
    );
  }

  return (
    <StaffPageShell
      title="Manage Students"
      subtitle="Manage student records, view details, and perform bulk operations"
      wide
    >
      <div className="mb-6">
        {/* Debug Information */}
        <div className="mt-2 text-sm text-gray-500">
          <p>Active Students: {students.length} | Soft-Deleted: {softDeletedStudents.length} | Filtered: {filteredStudents.length}</p>
          <p>Search Term: "{searchTerm}" | Role Filter: {filterRole} | Show Soft-Deleted: {showSoftDeleted ? 'Yes' : 'No'}</p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, email, student ID, or phone..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role
            </label>
            <SearchableSelect
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="w-full"
              required
              options={[
                { value: 'student', label: 'Students' },
                { value: 'trainer', label: 'Trainers' },
                { value: 'recruiter', label: 'Recruiters' },
                { value: 'admin', label: 'Admins' }
              ]}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Show Soft-Deleted
            </label>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="showSoftDeleted"
                checked={showSoftDeleted}
                onChange={toggleSoftDeleted}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="showSoftDeleted" className="ml-2 text-sm text-gray-700">
                Include soft-deleted students
              </label>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sort By
            </label>
            <SearchableSelect
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full"
              required
              options={[
                { value: 'fullName', label: 'Full Name' },
                { value: 'emailAddress', label: 'Email' },
                { value: 'createdAt', label: 'Created Date' },
                { value: 'country', label: 'Country' }
              ]}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Order
            </label>
            <SearchableSelect
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="w-full"
              required
              options={[
                { value: 'asc', label: 'Ascending' },
                { value: 'desc', label: 'Descending' }
              ]}
            />
          </div>
        </div>

        {/* Bulk delete selection limit */}
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-4">
          Bulk delete: select up to {selectionLimit} students at a time, then delete. Repeat for larger
          lists. You can also update <strong>Student Status</strong> per row or use{' '}
          <strong>Mark as Left</strong> for selected students.
        </p>

        {selectionWarning && (
          <p className="text-sm text-amber-900 bg-amber-100 border border-amber-300 rounded-md px-3 py-2 mb-4">
            {selectionWarning}
          </p>
        )}

        {/* Bulk Actions */}
        {selectedStudents.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 p-3 bg-blue-50 rounded-lg">
            <span className="text-sm font-medium text-blue-800">
              {selectedStudents.length} / {selectionLimit} student{selectedStudents.length !== 1 ? 's' : ''} selected
            </span>
            <button
              type="button"
              onClick={() => handleBulkMarkAsLeft(selectedStudents)}
              disabled={bulkStatusUpdating}
              className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {bulkStatusUpdating ? 'Updating…' : 'Mark as Left'}
            </button>
            {canDeleteStudents && (
              <button
                onClick={handleBulkDelete}
                disabled={selectedStudents.length > selectionLimit}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Delete Selected
              </button>
            )}
            <button
              onClick={clearSelection}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-sm"
            >
              Clear Selection
            </button>
          </div>
        )}
      </div>

      {/* Error Display */}
      {statusUpdateError && (
        <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-md">
          <h3 className="text-sm font-medium text-orange-800">Status update failed</h3>
          <p className="text-orange-700 mt-1 text-sm">{statusUpdateError}</p>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-red-800">Error Loading Students</h3>
              <p className="text-red-700 mt-1">{error}</p>
              <p className="text-xs text-red-600 mt-1">Check browser console for detailed error information.</p>
            </div>
            <button
              onClick={retryFetch}
              disabled={loading}
              className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50 text-sm"
            >
              {loading ? 'Retrying...' : 'Retry'}
            </button>
          </div>
          <div className="mt-2 text-xs text-red-600">
            Attempt {retryCount + 1}
          </div>
        </div>
      )}

      {/* Students Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={allSelectableSelected}
                    ref={(el) => {
                      if (el) {
                        const someSelected =
                          selectedStudents.length > 0 && !allSelectableSelected;
                        el.indeterminate = someSelected;
                      }
                    }}
                    onChange={() => toggleAllSelection(filteredStudents)}
                    title={`Select up to ${selectionLimit} students`}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sl. No.
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Full Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email Address
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Phone Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Student ID / Unix
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mode
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Course Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Batch Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Course Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Domain
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Batch Start
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Completion
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Follow-Up
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredStudents.map((student, index) => {
                const isSoftDeleted = student.isActive === false && student.deletedAt;
                const isLeftStudent = hasLeftEnrollment(student);
                const latestEnrollment = student.enrollments && student.enrollments.length > 0
                  ? student.enrollments[0]
                  : null;
                const batch = latestEnrollment?.batch;
                const course = batch?.course;
                const category = course?.category;
                const domain = student.domain;

                const counsellingSessions = latestEnrollment?.counsellingSessions || [];
                const latestCounselling = counsellingSessions.length > 0
                  ? counsellingSessions.slice(0, 4)
                  : [];

                const rowSelected = selectedStudents.some((s) => s.id === student.id);
                const rowCheckboxDisabled = selectionAtLimit && !rowSelected;

                return (
                <React.Fragment key={student.id}>
                <tr className={`hover:bg-gray-50 ${
                  isSoftDeleted
                    ? 'bg-red-50 border-l-4 border-red-400'
                    : isLeftStudent
                      ? 'bg-amber-50 border-l-4 border-amber-500'
                      : ''
                }`}>
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={rowSelected}
                      disabled={rowCheckboxDisabled}
                      title={rowCheckboxDisabled ? `Maximum ${selectionLimit} students can be selected` : undefined}
                      onChange={() => toggleStudentSelection(student)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-40"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {index + 1}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {getStudentFullName(student) || 'N/A'}
                      {isSoftDeleted && (
                        <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Deleted
                        </span>
                      )}
                      {!isSoftDeleted && isLeftStudent && (
                        <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-200 text-amber-900">
                          Left
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {getStudentEmail(student) || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {student.phoneNumber || student.mobile || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div>{student.id || 'N/A'}</div>
                    {student.unixUsername ? (
                      <div className="text-xs text-gray-500 font-mono mt-0.5">
                        {student.unixUsername}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {batch?.mode || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {batch?.location || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {category?.categoryName || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {batch?.batchName || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {course?.courseName || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {domain?.domainName || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {batch?.startDate 
                      ? new Date(batch.startDate).toLocaleDateString() 
                      : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {latestEnrollment?.courseCompletionDate 
                      ? new Date(latestEnrollment.courseCompletionDate).toLocaleDateString() 
                      : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm">
                      {/* Show counselling sessions info */}
                      {latestCounselling.length > 0 ? (
                        <div className="space-y-1">
                          {latestCounselling.map((session, idx) => (
                            <div key={idx} className="text-xs">
                              <div className="text-gray-900">
                                {session.sessionDate 
                                  ? new Date(session.sessionDate).toLocaleDateString() 
                                  : 'N/A'}
                              </div>
                              <div className="text-gray-600">
                                {session.counselor?.counselorName || session.counselor?.name || 'N/A'}
                              </div>
                              {session.remarks && (
                                <div className="text-gray-500 truncate max-w-xs" title={session.remarks}>
                                  {session.remarks.substring(0, 20)}...
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-500">No sessions</span>
                      )}
                      {/* Follow-up needed */}
                      {latestCounselling.some(s => s.followUpNeeded) && (
                        <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          Follow-Up
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <EnrollmentStatusControl
                      student={student}
                      onStatusChange={handleEnrollmentStatusChange}
                      updating={statusUpdatingIds.has(student.id)}
                      compact
                      disabled={isSoftDeleted}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex flex-wrap gap-2">
                      {canDeleteStudents && (
                        <button
                          type="button"
                          onClick={() => handleDeleteStudent(student)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => openAddEnrollmentModal(student)}
                        className="text-indigo-700 hover:text-indigo-900"
                        title="Enroll in an additional batch (keeps existing enrollments)"
                      >
                        Add enrollment
                      </button>
                      <button
                        type="button"
                        onClick={() => openChangeBatchModal(student)}
                        disabled={!student.enrollments?.length}
                        className="text-green-700 hover:text-green-900 disabled:opacity-40 disabled:cursor-not-allowed"
                        title={
                          student.enrollments?.length
                            ? 'Close current enrollment and enroll in another batch'
                            : 'No enrollment to move'
                        }
                      >
                        Change batch
                      </button>
                      <button
                        type="button"
                        onClick={() => setViewDetailStudent(student)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Same detail layout as Filter Users expanded row"
                      >
                        View
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const studentEmail = String(getStudentEmail(student) || '').trim();
                          const studentId = String(student.id || '').trim();
                          if (!studentEmail && !studentId) return;
                          const query = new URLSearchParams();
                          if (studentEmail) query.set('email', studentEmail.toLowerCase());
                          if (studentId) query.set('studentId', studentId);
                          navigate(`/admin/edit-user?${query.toString()}`);
                        }}
                        className="text-slate-600 hover:text-slate-900"
                        title="Open edit form"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setOpenEnrollmentDetailStudentId((id) =>
                            id === student.id ? null : student.id
                          )
                        }
                        className="text-purple-700 hover:text-purple-900"
                        title="Show every enrollment with batch, placement, fees, and sessions"
                      >
                        {openEnrollmentDetailStudentId === student.id ? 'Hide' : 'All'}{' '}
                        enrollments
                        {student.enrollments?.length ? ` (${student.enrollments.length})` : ''}
                      </button>
                    </div>
                  </td>
                </tr>
                {openEnrollmentDetailStudentId === student.id ? (
                  <tr className="bg-slate-50">
                    <td colSpan={TABLE_COL_SPAN} className="px-6 py-4 align-top">
                      <StudentEnrollmentsSections
                        enrollments={student.enrollments}
                        domain={domain}
                        repeatDomain={false}
                        onRemoveEnrollment={removeEnrollment}
                        removingEnrollmentId={removingEnrollmentId}
                      />
                    </td>
                  </tr>
                ) : null}
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredStudents.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No students found matching your criteria.
          </div>
        )}
      </div>

      {/* Delete Modal */}
      <StudentDeletionModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeleteStudent(null);
        }}
        student={deleteStudent}
        isBulk={!deleteStudent}
        selectedStudents={selectedStudents}
        onSuccess={handleDeleteSuccess}
        onError={handleDeleteError}
      />

      <ChangeBatchModal
        isOpen={!!changeBatchStudent}
        onClose={() => setChangeBatchStudent(null)}
        student={changeBatchStudent}
        batches={modalBatches}
        onSuccess={fetchStudents}
      />

      <AddEnrollmentModal
        isOpen={!!addEnrollmentStudent}
        onClose={() => setAddEnrollmentStudent(null)}
        studentId={addEnrollmentStudent?.id}
        enrollments={addEnrollmentStudent?.enrollments || []}
        batches={modalBatches}
        onSuccess={fetchStudents}
      />

      <ModalFrame
        isOpen={!!viewDetailStudent}
        onClose={() => setViewDetailStudent(null)}
        maxWidthClass="max-w-5xl"
        title={viewDetailStudent ? `Student — ${getStudentFullName(viewDetailStudent) || viewDetailStudent.id || 'Details'}` : ''}
        description="Same information as expanding a student on the Filter Users page."
        footer={
          <div className="flex flex-wrap justify-end gap-2 mt-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setViewDetailStudent(null)}
              className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
            {viewDetailStudent ? (
              <button
                type="button"
                onClick={() => {
                  const studentEmail = String(getStudentEmail(viewDetailStudent) || '').trim();
                  const studentId = String(viewDetailStudent.id || '').trim();
                  setViewDetailStudent(null);
                  if (!studentEmail && !studentId) return;
                  const query = new URLSearchParams();
                  if (studentEmail) query.set('email', studentEmail.toLowerCase());
                  if (studentId) query.set('studentId', studentId);
                  navigate(`/admin/edit-user?${query.toString()}`);
                }}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                Edit in form
              </button>
            ) : null}
          </div>
        }
      >
        {viewDetailStudent ? (
          <StudentDetailExpandedView student={viewDetailStudent} className="text-left" />
        ) : null}
      </ModalFrame>
    </StaffPageShell>
  );
};

export default StudentManagementPage;
